import {UrlCodec} from '../../utils/urlState';
import {
  PlayerBrowseState,
  PlayerDomain,
  PlayerReportsURLState,
  PlayerReportType,
  PlayerTab,
} from './types';

export const playerBrowseDefaults: PlayerBrowseState = {
  q: '',
  page: 1,
  pageSize: 20,
  sort: 'name',
  order: 'asc',
  playedLastMatch: false,
  watching: false,
  squadStatus: 'current',
};

const pageSizes = [20, 50, 100] as const;
const maxInt32 = 2147483647;
const sorts = ['name', 'age', 'position', 'team', 'latest_rating', 'report_recency'] as const;
const orders = ['asc', 'desc'] as const;
const positions = ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'] as const;
const availabilities = ['fit', 'injured', 'recovering', 'questionable'] as const;
const recentDayOptions = [7, 14, 30, 90] as const;
const squadStatuses = ['current', 'archived'] as const;
const playerTabs = ['overview', 'reports', 'documents', 'analysis'] as const;

const oneOf = <T extends string>(value: string | null, values: readonly T[]): T | undefined =>
  value !== null && values.includes(value as T) ? value as T : undefined;

const boundedInteger = (
  value: unknown,
  minimum: number,
  maximum = Number.MAX_SAFE_INTEGER,
): number | undefined => Number.isSafeInteger(value)
  && (value as number) >= minimum
  && (value as number) <= maximum
  ? value as number
  : undefined;

const safeInteger = (
  value: string | null,
  minimum: number,
  maximum = Number.MAX_SAFE_INTEGER,
): number | undefined => {
  if (value === null || !/^\d+$/.test(value)) return undefined;
  return boundedInteger(Number(value), minimum, maximum);
};

export const parsePlayerAgeFilter = (value: string | null): number | undefined =>
  safeInteger(value, 0, 100);

const boundedFloat = (
  value: unknown,
  minimum: number,
  maximum: number,
): number | undefined => typeof value === 'number'
  && Number.isFinite(value)
  && value >= minimum
  && value <= maximum
  ? value
  : undefined;

const safeFloat = (
  value: string | null,
  minimum: number,
  maximum: number,
): number | undefined => {
  if (value === null || !/^(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value)) return undefined;
  return boundedFloat(Number(value), minimum, maximum);
};

export const parsePlayerRatingFilter = (value: string | null): number | undefined =>
  safeFloat(value, 0, 100);

const optionalText = (value: string | null): string | undefined => value?.trim() || undefined;

const boundedPage = (value: unknown, pageSize: PlayerBrowseState['pageSize']): number | undefined => {
  const page = boundedInteger(value, 1);
  return page !== undefined && page - 1 <= Math.floor(maxInt32 / pageSize) ? page : undefined;
};

const createPlayerBrowseCodec = (defaultWatching: boolean, supportsWatching: boolean): UrlCodec<PlayerBrowseState> => ({
  parse(params) {
    const raw = {
      q: params.get('q'),
      page: params.get('page'),
      pageSize: params.get('page_size'),
      sort: params.get('sort'),
      order: params.get('order'),
      position: params.get('position'),
      team: params.get('team'),
      availability: params.get('availability'),
      minAge: params.get('min_age'),
      maxAge: params.get('max_age'),
      minRating: params.get('min_rating'),
      maxRating: params.get('max_rating'),
      recentDays: params.get('recent_days'),
      playedLastMatch: params.get('played_last_match'),
      squadStatus: params.get('squad_status'),
    };
    const watchingValues = supportsWatching ? params.getAll('watching') : [];
    const watching = watchingValues.length === 1
      && (watchingValues[0] === 'true' || watchingValues[0] === 'false')
      ? watchingValues[0] === 'true'
      : defaultWatching;
    const pageSize = safeInteger(raw.pageSize, 1);
    const selectedPageSize = pageSizes.includes(pageSize as PlayerBrowseState['pageSize'])
      ? pageSize as PlayerBrowseState['pageSize']
      : playerBrowseDefaults.pageSize;
    const page = safeInteger(raw.page, 1);
    const recentDays = safeInteger(raw.recentDays, 1);
    const position = oneOf(raw.position, positions);
    const team = optionalText(raw.team);
    const availability = oneOf(raw.availability, availabilities);
    const minAge = parsePlayerAgeFilter(raw.minAge);
    const maxAge = parsePlayerAgeFilter(raw.maxAge);
    const minRating = parsePlayerRatingFilter(raw.minRating);
    const maxRating = parsePlayerRatingFilter(raw.maxRating);
    const ageRangeValid = minAge === undefined || maxAge === undefined || minAge <= maxAge;
    const ratingRangeValid = minRating === undefined || maxRating === undefined || minRating <= maxRating;

    return {
      q: raw.q?.trim() ?? playerBrowseDefaults.q,
      page: boundedPage(page, selectedPageSize) ?? playerBrowseDefaults.page,
      pageSize: selectedPageSize,
      sort: oneOf(raw.sort, sorts) ?? playerBrowseDefaults.sort,
      order: oneOf(raw.order, orders) ?? playerBrowseDefaults.order,
      ...(position ? {position} : {}),
      ...(team ? {team} : {}),
      ...(availability ? {availability} : {}),
      ...(ageRangeValid && minAge !== undefined ? {minAge} : {}),
      ...(ageRangeValid && maxAge !== undefined ? {maxAge} : {}),
      ...(ratingRangeValid && minRating !== undefined ? {minRating} : {}),
      ...(ratingRangeValid && maxRating !== undefined ? {maxRating} : {}),
      ...(recentDayOptions.includes(recentDays as typeof recentDayOptions[number])
        ? {recentDays: recentDays as PlayerBrowseState['recentDays']}
        : {}),
      playedLastMatch: raw.playedLastMatch === 'true',
      watching,
      squadStatus: oneOf(raw.squadStatus, squadStatuses) ?? playerBrowseDefaults.squadStatus,
    };
  },

  serialize(value) {
    const params = new URLSearchParams();
    const pageSize = pageSizes.includes(value.pageSize) ? value.pageSize : playerBrowseDefaults.pageSize;
    const page = boundedPage(value.page, pageSize);
    const sort = oneOf(value.sort, sorts);
    const order = oneOf(value.order, orders);
    const position = value.position ? oneOf(value.position, positions) : undefined;
    const availability = value.availability ? oneOf(value.availability, availabilities) : undefined;
    const minAge = boundedInteger(value.minAge, 0, 100);
    const maxAge = boundedInteger(value.maxAge, 0, 100);
    const minRating = boundedFloat(value.minRating, 0, 100);
    const maxRating = boundedFloat(value.maxRating, 0, 100);
    const ageRangeValid = minAge === undefined || maxAge === undefined || minAge <= maxAge;
    const ratingRangeValid = minRating === undefined || maxRating === undefined || minRating <= maxRating;
    const recentDays = value.recentDays !== undefined
      && recentDayOptions.includes(value.recentDays)
      ? value.recentDays
      : undefined;

    if (value.q) params.set('q', value.q);
    if (page !== undefined && page !== playerBrowseDefaults.page) params.set('page', String(page));
    if (pageSize !== playerBrowseDefaults.pageSize) params.set('page_size', String(pageSize));
    if (sort !== undefined && sort !== playerBrowseDefaults.sort) params.set('sort', sort);
    if (order !== undefined && order !== playerBrowseDefaults.order) params.set('order', order);
    if (position) params.set('position', position);
    if (value.team) params.set('team', value.team);
    if (availability) params.set('availability', availability);
    if (ageRangeValid && minAge !== undefined) params.set('min_age', String(minAge));
    if (ageRangeValid && maxAge !== undefined) params.set('max_age', String(maxAge));
    if (ratingRangeValid && minRating !== undefined) params.set('min_rating', String(minRating));
    if (ratingRangeValid && maxRating !== undefined) params.set('max_rating', String(maxRating));
    if (recentDays !== undefined) params.set('recent_days', String(recentDays));
    if (value.playedLastMatch === true) params.set('played_last_match', 'true');
    if (supportsWatching && typeof value.watching === 'boolean' && value.watching !== defaultWatching) {
      params.set('watching', String(value.watching));
    }
    if (value.squadStatus === 'archived') params.set('squad_status', 'archived');
    return params;
  },
});

export const playerBrowseCodec = createPlayerBrowseCodec(false, false);
export const scoutingPlayerBrowseDefaults: PlayerBrowseState = {...playerBrowseDefaults, watching: true};
export const scoutingPlayerBrowseCodec = createPlayerBrowseCodec(true, true);

export const parsePlayerTab = (value: string | null, allowedTabs: readonly string[] = playerTabs): PlayerTab => {
  const parsed = oneOf(value, playerTabs);
  return parsed && allowedTabs.includes(parsed) ? parsed : 'overview';
};

export const parsePlayerReportsURLState = (
  params: URLSearchParams,
  readable: readonly PlayerReportType[],
): PlayerReportsURLState => {
  const requested = params.get('type');
  const type = requested && readable.includes(requested as PlayerReportType)
    ? requested as PlayerReportType
    : 'all';
  const rawPage = params.get('page');
  const parsedPage = rawPage === null ? 1 : Number(rawPage);
  return {type, page: Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1};
};

export const serializePlayerReportsURLState = (
  current: URLSearchParams,
  state: PlayerReportsURLState,
): URLSearchParams => {
  const params = new URLSearchParams(current);
  params.set('tab', 'reports');
  if (state.type === 'all') params.delete('type');
  else params.set('type', state.type);
  if (state.page === 1) params.delete('page');
  else params.set('page', String(state.page));
  return params;
};

const playerDomainRoots: Record<PlayerDomain, string> = {
  squad: '/players',
  scouting: '/scouting',
};

export const parsePlayerReturnTo = (value: string | null, domain: PlayerDomain): string => {
  const root = playerDomainRoots[domain];
  if (!value?.startsWith(root)) return root;

  try {
    const parsed = new URL(value, 'https://local.invalid');
    return parsed.origin === 'https://local.invalid'
      && parsed.pathname === root
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : root;
  } catch {
    return root;
  }
};
